import React, { useState } from 'react';
import { signUp } from 'aws-amplify/auth';
import { uploadData } from 'aws-amplify/storage';
import { getCitiesForCountry, getAvailableCountries } from '../data/citiesData';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe('pk_live_51MeuHnBq44xeUkkuos2fn9jP9LKxs6qoKtfLPvAFNXwBmrGcdwd4LlvSUTLcz4rqu5znMdgTN1mdqa8cG0367LgG00KkKSwqkj');

const ageGroups = [
  { value: '', label: 'Select your age group' },
  { value: '18-24', label: '18-24' },
  { value: '25-34', label: '25-34' },
  { value: '35-44', label: '35-44' },
  { value: '45-54', label: '45-54' },
  { value: '55+', label: '55+' },
];

const countries = getAvailableCountries();

const subscriptionPlans = [
  {
    id: 'free',
    name: 'Free',
    price: '£0',
    period: 'forever',
    features: [
      'Take personality test',
      'Read travel blog',
      'Basic profile creation',
      'Limited messaging (3/day)',
      'View member profiles'
    ],
    popular: false,
    cta: 'Start Free',
    disabled: false
  },
  {
    id: 'premium',
    name: 'Premium',
    price: '£9.99',
    period: 'per month',
    features: [
      'Everything in Free',
      'Unlimited messaging',
      'Search & filter members',
      'Organize local meets',
      'Join travel groups',
      'Priority support',
      'Advanced matching',
      'Travel deals access'
    ],
    popular: true,
    cta: 'Start Free Trial',
    disabled: false
  },
  {
    id: 'annual',
    name: 'Annual',
    price: '£99',
    period: 'per year',
    features: [
      'Everything in Premium',
      '2 months free',
      'Exclusive travel events',
      'VIP support',
      'Early access to features',
      'Custom travel planning'
    ],
    popular: false,
    cta: 'Save 17%',
    disabled: false
  }
];

export default function SignupForm({ onSuccess }) {
  const [form, setForm] = useState({
    name: '',
    username: '',
    email: '',
    city: '',
    country: 'United Kingdom',
    ageGroup: '',
    phone: '',
    password: '',
    confirmPassword: '',
    photo: null,
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('free');
  const [showPaymentStep, setShowPaymentStep] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    
    // Auto-generate username from email if username is empty
    if (name === 'email' && !form.username) {
      const emailName = value.split('@')[0];
      if (emailName) {
        setForm(prev => ({ 
          ...prev, 
          [name]: value,
          username: emailName.charAt(0).toUpperCase() + emailName.slice(1).replace(/[0-9]/g, '')
        }));
      }
    }
    
    // Auto-generate name from email if name is empty
    if (name === 'email' && !form.name) {
      const emailName = value.split('@')[0];
      if (emailName) {
        const displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1).replace(/[0-9]/g, '');
        if (displayName.length >= 2) {
          setForm(prev => ({ 
            ...prev, 
            [name]: value,
            name: displayName
          }));
        }
      }
    }
    
    setErrorMsg('');
  }

  function handlePhotoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErrorMsg('Please select a valid image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrorMsg('Image must be under 5MB.');
      return;
    }
    setForm(f => ({ ...f, photo: file }));
    setPhotoPreview(URL.createObjectURL(file));
    setErrorMsg('');
  }

  async function uploadPhotoToS3(file) {
    try {
      // Generate unique filename
      const timestamp = Date.now();
      const fileName = `profile-photos/${timestamp}-${file.name}`;
      
      // Upload to S3
      const result = await uploadData({
        key: fileName,
        data: file,
        options: {
          contentType: file.type,
          accessLevel: 'guest'
        }
      });
      
      return result.key;
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw new Error('Failed to upload photo. Please try again.');
    }
  }

  function validateForm() {
    if (!form.name.trim()) {
      setErrorMsg('Name is required.');
      return false;
    }
    if (!form.username.trim()) {
      setErrorMsg('Username is required.');
      return false;
    }
    if (!form.email.trim()) {
      setErrorMsg('Email is required.');
      return false;
    }
    if (!form.password) {
      setErrorMsg('Password is required.');
      return false;
    }
    if (form.password !== form.confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return false;
    }
    if (form.password.length < 8) {
      setErrorMsg('Password must be at least 8 characters long.');
      return false;
    }
    if (!form.ageGroup) {
      setErrorMsg('Please select your age group.');
      return false;
    }
    return true;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrorMsg('');

    try {
      let photoKey = null;
      if (form.photo) {
        photoKey = await uploadPhotoToS3(form.photo);
      }

      // Create user attributes
      const userAttributes = {
        email: form.email,
        name: form.name,
        'custom:username': form.username,
        'custom:city': form.city,
        'custom:country': form.country,
        'custom:ageGroup': form.ageGroup,
        'custom:phone': form.phone,
        'custom:photoKey': photoKey,
        'custom:subscriptionPlan': selectedPlan
      };

      // Sign up user
      const { isSignUpComplete, userId } = await signUp({
        username: form.username,
        password: form.password,
        options: {
          userAttributes
        }
      });

      if (isSignUpComplete) {
        setSuccessMsg('Account created successfully! Please check your email to verify your account.');
        setFormSubmitted(true);
        
        // If premium plan selected, proceed to payment
        if (selectedPlan !== 'free') {
          setShowPaymentStep(true);
        } else {
          onSuccess && onSuccess();
        }
      }
    } catch (error) {
      console.error('Signup error:', error);
      if (error.name === 'UsernameExistsException') {
        setErrorMsg('Username already exists. Please choose a different username.');
      } else if (error.name === 'InvalidPasswordException') {
        setErrorMsg('Password must be at least 8 characters and contain uppercase, lowercase, and numbers.');
      } else {
        setErrorMsg('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  const handlePlanSelection = (planId) => {
    setSelectedPlan(planId);
  };

  const handlePayment = async () => {
    if (selectedPlan === 'free') {
      onSuccess && onSuccess();
      return;
    }

    setPaymentLoading(true);
    setErrorMsg('');

    try {
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      // Create checkout session
      const response = await fetch('/api/stripe-checkout/create-session.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan: selectedPlan,
          price: selectedPlan === 'premium' ? 9.99 : 99,
          currency: 'gbp',
          feature: 'subscription-signup',
          email: form.email,
          username: form.username
        }),
      });

      const { sessionId } = await response.json();

      // Redirect to Stripe checkout
      const { error } = await stripe.redirectToCheckout({
        sessionId,
      });

      if (error) {
        throw new Error(error.message);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setErrorMsg('Payment failed. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  if (showPaymentStep) {
    return (
      <div className="container" style={{ padding: 'var(--spacing-8) 0' }}>
        <div className="row justify-center">
          <div className="col col--8">
            <div className="card p-6">
              <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-6)' }}>
                <h1>Choose Your Plan</h1>
                <p style={{ fontSize: '1.2em', color: 'var(--gray-600)' }}>
                  Welcome {form.name}! Choose your subscription plan to get started.
                </p>
              </div>

              {errorMsg && (
                <div style={{
                  background: 'var(--error-red)',
                  color: 'white',
                  padding: 'var(--spacing-3)',
                  borderRadius: 'var(--radius-sm)',
                  marginBottom: 'var(--spacing-4)'
                }}>
                  {errorMsg}
                </div>
              )}

              <div className="row" style={{ gap: 'var(--spacing-4)' }}>
                {subscriptionPlans.map((plan, index) => (
                  <div key={index} className="col col--4" style={{ 
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div className="card p-4" style={{
                      height: '100%',
                      position: 'relative',
                      border: plan.popular ? '3px solid var(--primary-blue)' : '1px solid var(--gray-200)',
                      transform: plan.popular ? 'scale(1.05)' : 'none',
                      zIndex: plan.popular ? 1 : 0,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => handlePlanSelection(plan.id)}
                    onKeyDown={(e) => e.key === 'Enter' && handlePlanSelection(plan.id)}
                    tabIndex={0}
                    role="button"
                    aria-label={`Select ${plan.name} plan`}
                    >
                      {plan.popular && (
                        <div style={{
                          position: 'absolute',
                          top: '-12px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: 'var(--primary-blue)',
                          color: 'white',
                          padding: 'var(--spacing-1) var(--spacing-3)',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'bold'
                        }}>
                          MOST POPULAR
                        </div>
                      )}

                      <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-4)' }}>
                        <h3 style={{ marginBottom: 'var(--spacing-2)' }}>{plan.name}</h3>
                        <div style={{ fontSize: '2.5em', fontWeight: 'bold', margin: 'var(--spacing-2) 0' }}>
                          {plan.price}
                        </div>
                        <p style={{ color: 'var(--gray-600)' }}>{plan.period}</p>
                      </div>

                      <ul style={{ 
                        textAlign: 'left', 
                        marginBottom: 'var(--spacing-4)',
                        flex: 1,
                        listStyle: 'none',
                        padding: 0
                      }}>
                        {plan.features.map((feature, idx) => (
                          <li key={idx} style={{ 
                            marginBottom: 'var(--spacing-2)',
                            paddingLeft: 'var(--spacing-4)',
                            position: 'relative'
                          }}>
                            <span style={{
                              position: 'absolute',
                              left: 0,
                              color: 'var(--primary-blue)'
                            }}>✓</span>
                            {feature}
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlanSelection(plan.id);
                          handlePayment();
                        }}
                        disabled={paymentLoading}
                        className="btn btn--primary"
                        style={{ 
                          width: '100%',
                          background: selectedPlan === plan.id ? 'var(--primary-blue)' : 'var(--gray-300)',
                          color: selectedPlan === plan.id ? 'white' : 'var(--gray-700)'
                        }}
                      >
                        {paymentLoading ? 'Processing...' : plan.cta}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ textAlign: 'center', marginTop: 'var(--spacing-6)' }}>
                <button
                  onClick={() => setShowPaymentStep(false)}
                  className="btn btn--secondary"
                >
                  ← Back to Signup
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: 'var(--spacing-8) 0' }}>
      <div className="row justify-center">
        <div className="col col--8">
          <div className="card p-6">
            <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-6)' }}>
              <h1>Create Your Account</h1>
              <p style={{ fontSize: '1.2em', color: 'var(--gray-600)' }}>
                Join GradedWash and find your perfect travel companion
              </p>
            </div>

            {errorMsg && (
              <div style={{
                background: 'var(--error-red)',
                color: 'white',
                padding: 'var(--spacing-3)',
                borderRadius: 'var(--radius-sm)',
                marginBottom: 'var(--spacing-4)'
              }}>
                {errorMsg}
              </div>
            )}

            {successMsg && (
              <div style={{
                background: 'var(--success-green)',
                color: 'white',
                padding: 'var(--spacing-3)',
                borderRadius: 'var(--radius-sm)',
                marginBottom: 'var(--spacing-4)'
              }}>
                {successMsg}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="row" style={{ gap: 'var(--spacing-4)' }}>
                <div className="col col--6">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      className="form-input"
                      required
                      aria-describedby="name-help"
                    />
                    <small id="name-help">Your display name on the platform</small>
                  </div>
                </div>

                <div className="col col--6">
                  <div className="form-group">
                    <label htmlFor="username">Username *</label>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={form.username}
                      onChange={handleChange}
                      className="form-input"
                      required
                      aria-describedby="username-help"
                    />
                    <small id="username-help">Unique username for your profile</small>
                  </div>
                </div>
              </div>

              <div className="row" style={{ gap: 'var(--spacing-4)' }}>
                <div className="col col--6">
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      className="form-input"
                      required
                      aria-describedby="email-help"
                    />
                    <small id="email-help">We'll send verification to this email</small>
                  </div>
                </div>

                <div className="col col--6">
                  <div className="form-group">
                    <label htmlFor="ageGroup">Age Group *</label>
                    <select
                      id="ageGroup"
                      name="ageGroup"
                      value={form.ageGroup}
                      onChange={handleChange}
                      className="form-input"
                      required
                      aria-describedby="age-help"
                    >
                      {ageGroups.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <small id="age-help">For better matching with travel companions</small>
                  </div>
                </div>
              </div>

              <div className="row" style={{ gap: 'var(--spacing-4)' }}>
                <div className="col col--6">
                  <div className="form-group">
                    <label htmlFor="country">Country *</label>
                    <select
                      id="country"
                      name="country"
                      value={form.country}
                      onChange={handleChange}
                      className="form-input"
                      required
                      aria-describedby="country-help"
                    >
                      {countries.map(country => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                    <small id="country-help">Your home country</small>
                  </div>
                </div>

                <div className="col col--6">
                  <div className="form-group">
                    <label htmlFor="city">City</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={form.city}
                      onChange={handleChange}
                      className="form-input"
                      aria-describedby="city-help"
                    />
                    <small id="city-help">Your current city (optional)</small>
                  </div>
                </div>
              </div>

              <div className="row" style={{ gap: 'var(--spacing-4)' }}>
                <div className="col col--6">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      className="form-input"
                      aria-describedby="phone-help"
                    />
                    <small id="phone-help">For local meetups and travel coordination</small>
                  </div>
                </div>

                <div className="col col--6">
                  <div className="form-group">
                    <label htmlFor="photo">Profile Photo</label>
                    <input
                      type="file"
                      id="photo"
                      name="photo"
                      onChange={handlePhotoChange}
                      accept="image/*"
                      className="form-input"
                      aria-describedby="photo-help"
                    />
                    <small id="photo-help">Upload a clear photo of yourself (optional)</small>
                  </div>
                </div>
              </div>

              {photoPreview && (
                <div style={{ marginBottom: 'var(--spacing-4)' }}>
                  <img 
                    src={photoPreview} 
                    alt="Profile preview" 
                    style={{ 
                      maxWidth: '150px', 
                      maxHeight: '150px', 
                      borderRadius: '50%',
                      border: '3px solid var(--primary-blue)'
                    }} 
                  />
                </div>
              )}

              <div className="row" style={{ gap: 'var(--spacing-4)' }}>
                <div className="col col--6">
                  <div className="form-group">
                    <label htmlFor="password">Password *</label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      className="form-input"
                      required
                      aria-describedby="password-help"
                    />
                    <small id="password-help">Minimum 8 characters</small>
                  </div>
                </div>

                <div className="col col--6">
                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password *</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={form.confirmPassword}
                      onChange={handleChange}
                      className="form-input"
                      required
                      aria-describedby="confirm-help"
                    />
                    <small id="confirm-help">Must match your password</small>
                  </div>
                </div>
              </div>

              <div style={{ textAlign: 'center', marginTop: 'var(--spacing-6)' }}>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn--primary"
                  style={{ fontSize: '1.2em', padding: 'var(--spacing-3) var(--spacing-6)' }}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

